import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from './../components/Message';
import Loader from './../components/Loader';
import { useGetStocksQuery, useDeleteStockMutation } from './../slices/stocksApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';

const StaffStock = () => {
  // State variables for filtering, pagination, and date range
  const [filter, setFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch paginated stocks data
  const { data, isLoading, error, refetch } = useGetStocksQuery(pageNumber);

  // Mutation for deleting stocks
  const [deleteStock, { isLoading: loadingDelete }] = useDeleteStockMutation();

  // Handle stock deletion
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this stock?')) {
      try {
        await deleteStock(id).unwrap();
        toast.success('Stock deleted successfully!');
        refetch(); // Refetch stocks after deletion
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete stock.');
      }
    }
  };

  // Filter stocks based on date range and search criteria
  const filterStocks = () => {
    if (!data?.stocks) return [];

    // 1) Filter by date range
    let filteredStocks = data.stocks.filter((stock) => {
      const stockDate = new Date(stock.date);
      return (
        (!startDate || stockDate >= new Date(startDate)) &&
        (!endDate || stockDate <= new Date(endDate))
      );
    });

    // 2) Apply general search filter against stock note and product names within stockItems.
    if (filter.trim()) {
      const lowerFilter = filter.toLowerCase();
      filteredStocks = filteredStocks.filter((stock) => {
        const noteMatch = stock.note?.toLowerCase().includes(lowerFilter);
        const itemMatch = stock.stockItems.some((item) =>
          item.name.toLowerCase().includes(lowerFilter)
        );
        return noteMatch || itemMatch;
      });
    }

    // 3) Further filter stock items by product name ONLY using productSearch.
    if (productSearch.trim()) {
      const lowerProduct = productSearch.toLowerCase();
      filteredStocks = filteredStocks
        .map((stock) => {
          return {
            ...stock,
            stockItems: stock.stockItems.filter((item) =>
              item.name.toLowerCase().includes(lowerProduct)
            ),
          };
        })
        .filter((stock) => stock.stockItems.length > 0);
    }

    return filteredStocks;
  };

  // Calculate total stocks value based on filtered stocks
  const calculateTotalStocks = () => {
    return filterStocks().reduce((total, stock) => {
      const stockTotal = stock.stockItems.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      );
      return total + stockTotal;
    }, 0);
  };

  // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>Stocks</h1>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            placeholder="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            placeholder="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total Stocks: {calculateTotalStocks().toFixed(2)}</h4>
        </Col>
      </Row>

      {/* Additional product-only search box */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by product name only"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT NAME</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterStocks().map((stock) =>
                stock.stockItems.map((item) => (
                  <tr key={`${stock._id}-${item.product}`}>
                    <td>{new Date(stock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>{(item.qty * item.price).toFixed(2)}</td>
                    <td>{stock.note || 'N/A'}</td>
                    <td>
                      {item.images && (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          className="thumbnail-img me-2"
                          style={{
                            width: '10px',
                            height: '10px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/stock/${stock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        as={Link}
                        to={`/stock/${stock._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                        as={Link}
                        to={`/copy/stock/${stock._id}/copy`}
                        variant="light"
                      className="btn-sm mx-2"
                       >
                      Copy
                    </Button>
                      <Button
                        style={{ display: 'none' }}
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(stock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StaffStock;